import type { StringArrayCondition } from "@skilitics/judy";

import type { ConditionBuilder } from "../../../../types";
import { operatorToEnglish } from "../../helpers";
import { ConditionBuilderSummary } from "../../ui/Summary";
import type { StringArrayConditionFormValues } from "./StringArrayConditionForm";
import { StringArrayConditionForm } from "./StringArrayConditionForm";
import type { StringArrayConditionContext } from "./types";

export const stringArrayConditionBuilder: ConditionBuilder<StringArrayConditionContext> = {
  typeId: "stringArray",
  defaultCategory: "Activity",
  build: ({ config: { operator, value }, spec: { property }, resultProperty }) => {
    if (!stringArrayConditionBuilder.isValid?.({ operator, value })) return;

    if (!(operator && value !== undefined)) return;
    return {
      type: "stringArray",
      subject: resultProperty(property, "stringArray"),
      operator,
      object: value,
    } as StringArrayCondition;
  },
  isValid: ({ operator, value }) => typeof value === "string" && operator !== undefined,
  getInitialConfig: () => ({}),
  views: {
    summary: ({ spec: { title, options, operator: specOperator, summaryTemplate }, config: { operator, value } }) => {
      const effectiveOperator = specOperator ?? operator;
      const operatorLabel = operatorToEnglish(effectiveOperator);
      const valueLabel = options?.find((o) => o.value === value)?.label ?? value;

      const template = summaryTemplate
        ? () => summaryTemplate({ title, operator: effectiveOperator, operatorLabel, value, valueLabel })
        : undefined;

      return <ConditionBuilderSummary title={title} operator={operatorLabel} value={valueLabel} template={template} />;
    },
    edit: ({ spec: { options, operator: specOperator }, config: { value, operator }, onConfigChange }) => {
      const initialValues: StringArrayConditionFormValues = {
        operator: specOperator ?? operator ?? "",
        value: value ?? "",
      };

      const handleSubmit = (values: StringArrayConditionFormValues) => {
        onConfigChange({
          operator: values.operator || undefined,
          value: values.value,
        });
      };

      return (
        <StringArrayConditionForm
          initialValues={initialValues}
          onSubmit={handleSubmit}
          options={options}
          specOperator={specOperator}
        />
      );
    },
  },
};

export type StringArrayConditionBuilder = typeof stringArrayConditionBuilder;
